import React from 'react'
import { Heading, Button } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  ResidentAppBackLink,
  ResidentAppCard,
  ResidentAppView,
} from '@pandiafi/components'

import { useContractPaths } from '../../hooks/useContractPaths/useContractPaths'
import routePath from '../../router/routePath'
import { useCurrentContract } from '../../hooks/useCurrentContract/useCurrentContract'
import { TContact } from '../../../common/validations/contractContacts'
import { ContactCategory } from '../../../common/types'
import { ContactInfoSidebarDataLoader } from './ContactInfoSidebarDataLoader'

import { ContactCard } from 'components/ContactCard/ContactCard'
import { ContactInfoNoticesList } from 'components/ContactInfoNoticesList'
import { useModuleConfig } from 'hooks/useModuleConfig/useModuleConfig'

export const ContactInfoView = ({ contacts }: { contacts: TContact[] }) => {
  const { t } = useTranslation(['common', 'titles', 'contact'])
  const { frontPagePath } = useContractPaths()
  const navigate = useNavigate()
  const { currentContractId } = useCurrentContract()
  const modules = useModuleConfig()

  const {
    propertyManager,
    propertyMaintenance,
    propertySecretaries,
    customerService,
    salesRepresentative,
    residentsCommittee,
  } = resolveContacts(contacts)

  const hasDefectNotice =
    (modules.defectNotice.enabled &&
      !modules.defectNotice.customDefectNoticeLinkUrl) ??
    false
  const hasFeedbackOrQuestion =
    modules.defectNotice.feedback || modules.defectNotice.question

  return (
    <ResidentAppView
      title={t('contact:contactInformation')}
      backLink={
        <ResidentAppBackLink href={frontPagePath}>
          {t('common:views.home')}
        </ResidentAppBackLink>
      }
      aside={
        hasDefectNotice &&
        hasFeedbackOrQuestion && (
          <ContactInfoSidebarDataLoader>
            {({ feedbackAndQuestionNotices }) => (
              <ContactInfoNoticesList notices={feedbackAndQuestionNotices} />
            )}
          </ContactInfoSidebarDataLoader>
        )
      }
    >
      <ResidentAppCard>
        {propertyMaintenance && (
          <ContactCard
            className="mb-8"
            title={propertyMaintenance.officeName}
            contact={propertyMaintenance}
            showName
            role={t('contact:propertyMaintenance')}
          />
        )}

        {propertyManager && (
          <ContactCard
            className="mb-8"
            title={propertyManager.officeName}
            contact={propertyManager}
            showName
            role={t('contact:propertyManager')}
          />
        )}

        {/* Property company is already shown in the property manager's title */}
        {propertySecretaries?.map((propertySecretary) => (
          <ContactCard
            className="mb-8"
            key={propertySecretary.id}
            title={propertyManager ? null : propertySecretary.officeName}
            contact={propertySecretary}
            showName
            role={t('contact:propertySecretary')}
          />
        ))}

        {customerService && (
          <ContactCard
            className="mb-8"
            title={t('contact:customerService')}
            contact={customerService}
            showName={false}
            role={null}
          />
        )}

        {hasDefectNotice && hasFeedbackOrQuestion && (
          <div className="mb-8 flex flex-col sm:flex-row gap-4">
            {modules.defectNotice.feedback && (
              <Button
                onClick={() =>
                  navigate(
                    routePath.feedback.replace(
                      ':contractId',
                      currentContractId
                    ),
                    { state: { from: 'contact' } }
                  )
                }
                variant="secondary"
                className="w-full"
              >
                {t('contact:feedback.button')}
              </Button>
            )}
            {modules.defectNotice.question && (
              <Button
                onClick={() =>
                  navigate(
                    routePath.question.replace(
                      ':contractId',
                      currentContractId
                    ),
                    { state: { from: 'contact' } }
                  )
                }
                variant="secondary"
                className="w-full"
              >
                {t('contact:question.button')}
              </Button>
            )}
          </div>
        )}

        {salesRepresentative && (
          <ContactCard
            className="mb-8"
            title={t('common:buildingInfo.sales')}
            contact={salesRepresentative}
            showName
            role={t('common:buildingInfo.sales')}
          />
        )}

        {residentsCommittee && residentsCommittee.length > 0 && (
          <div className="mb-8">
            <Heading element="h2" size="l" className="my-4">
              {t('contact:residentsCommittee')}
            </Heading>
            <div className="flex flex-col gap-4">
              {residentsCommittee.map((member) => (
                <ContactCard
                  key={member.id}
                  title={null}
                  contact={member}
                  showName
                  role={member.serviceRole}
                />
              ))}
            </div>
          </div>
        )}

        {(!contacts || contacts.length === 0) && (
          <div className="text-center py-8 text-common-gray-6">
            {t('common:buildingInfo.noContactInformation')}
          </div>
        )}
      </ResidentAppCard>
    </ResidentAppView>
  )
}

const resolveContacts = (contacts?: TContact[]) => {
  const propertyManager = contacts?.find(
    (c) => c.contactCategory === ContactCategory.PROPERTY_MANAGEMENT
  )
  const propertyMaintenance = contacts?.find(
    (c) => c.contactCategory === ContactCategory.PROPERTY_MAINTENANCE
  )
  const propertySecretaries = contacts?.filter(
    (c) => c.contactCategory === ContactCategory.PROPERTY_SECRETARY
  )
  const customerService = contacts?.find(
    (c) => c.contactCategory === ContactCategory.CUSTOMER_SERVICE
  )
  const salesRepresentative = contacts?.find(
    (c) => c.contactCategory === ContactCategory.SALES_REPRESENTATIVE
  )

  // Filter out members of the residents' committee
  const residentsCommittee = contacts?.filter(
    (c) => c.contactCategory === ContactCategory.RESIDENT_COMMITTEE
  )

  return {
    propertyManager,
    propertyMaintenance,
    propertySecretaries,
    customerService,
    salesRepresentative,
    residentsCommittee,
  }
}
